import gql from 'graphql-tag';

import GraphQLClient, { GraphQLOperationFailed } from '../../adapter/GraphQLClient';
import {
  CreateModificationRequestDeliverableMutation,
  CreateModificationRequestItemIssueCommentMutation,
  CreateModificationRequestItemIssueMutation,
  ReopenModificationRequestIssueMutation,
  TouchToTheModificationRequestMutation,
} from '../../graphql/generated/graphql';

class ModificationRequestWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public touchToTheModificationRequest = async (
    id: string,
  ): Promise<NonNullable<TouchToTheModificationRequestMutation['touchToTheModificationRequest']>> => {
    const result: TouchToTheModificationRequestMutation = await this.graphqlClient.query(
      gql`
        mutation touchToTheModificationRequest($id: ID!) {
          touchToTheModificationRequest(id: $id) {
            id
            bucketId
            state
            targetDeliverableId
            feeType
            createdAt
            submittedAt
            deliveryDue
            feedback {
              id
              body
              createdAt
            }
            sender {
              id
              name
              profileImageUrl
            }
            items {
              id
              bucketId
              type
              typeName
              assetIds
              body
              metadata
              sender {
                id
                name
                profileImageUrl
              }
              issue {
                id
                name
                resolved
                comments {
                  id
                  body
                  assetIds
                  createdAt
                  user {
                    ... on UserDirector {
                      id
                      name
                      email
                      profileImageUrl
                    }
                    ... on UserOptimizer {
                      id
                      name
                      email
                      profileImageUrl
                    }
                  }
                }
              }
            }
            deliverables {
              id
              createdAt
              creative {
                id
                file {
                  originalFileName
                  url
                  mimeType
                  attribute {
                    aspectRatio {
                      size {
                        height
                        width
                      }
                    }
                    playbackTime {
                      seconds
                    }
                    volume {
                      maxVolume
                    }
                  }
                  fastStart {
                    enabled
                  }
                }
              }
              attachedThumbnail {
                id
                file {
                  originalFileName
                  url
                  mimeType
                  size
                  attribute {
                    aspectRatio {
                      size {
                        height
                        width
                      }
                    }
                    playbackTime {
                      seconds
                    }
                    volume {
                      maxVolume
                    }
                  }
                  fastStart {
                    enabled
                  }
                }
              }
              intent
            }
          }
        }
      `,
      { id },
    );

    if (result.touchToTheModificationRequest) {
      return result.touchToTheModificationRequest;
    } else {
      throw new Error('touchToTheModificationRequest is blank');
    }
  };

  public createModificationRequestItemIssue = async (
    modificationRequestItemId: string,
    name: string,
    body: string,
  ): Promise<NonNullable<CreateModificationRequestItemIssueMutation['createModificationRequestItemIssue']>> => {
    const result: CreateModificationRequestItemIssueMutation = await this.graphqlClient.query(
      gql`
        mutation createModificationRequestItemIssue($modificationRequestItemId: ID!, $name: String!, $body: String!) {
          createModificationRequestItemIssue(
            modificationRequestItemId: $modificationRequestItemId
            name: $name
            body: $body
          ) {
            id
            name
            resolved
            comments {
              id
              body
              assetIds
              createdAt
              user {
                ... on UserDirector {
                  id
                  name
                  email
                  profileImageUrl
                }
                ... on UserOptimizer {
                  id
                  name
                  email
                  profileImageUrl
                }
              }
            }
          }
        }
      `,
      { modificationRequestItemId, name, body },
    );

    if (result.createModificationRequestItemIssue) {
      return result.createModificationRequestItemIssue;
    } else {
      throw new Error('createModificationRequestItemIssue is blankn');
    }
  };

  public createModificationRequestItemIssueComment = async (
    modificationRequestIssueId: string,
    body: string,
    bucketId: string | null,
    assetIds: string[],
  ): Promise<
    NonNullable<CreateModificationRequestItemIssueCommentMutation['createModificationRequestItemIssueComment']>
  > => {
    const result: CreateModificationRequestItemIssueCommentMutation = await this.graphqlClient.query(
      gql`
        mutation createModificationRequestItemIssueComment(
          $issueId: ID!
          $body: String!
          $bucketId: String
          $assetIds: [String!]!
        ) {
          createModificationRequestItemIssueComment(
            issueId: $issueId
            body: $body
            bucketId: $bucketId
            assetIds: $assetIds
          ) {
            id
            body
            assetIds
            createdAt
            user {
              ... on UserDirector {
                id
                name
                email
                profileImageUrl
              }
              ... on UserOptimizer {
                id
                name
                email
                profileImageUrl
              }
            }
          }
        }
      `,
      { issueId: modificationRequestIssueId, body, bucketId, assetIds },
    );

    if (result.createModificationRequestItemIssueComment) {
      return result.createModificationRequestItemIssueComment;
    } else {
      throw new Error('createModificationRequestItemIssueComment is blank');
    }
  };

  public resolveModificationRequestIssue = async (modificationRequestIssueId: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation resolveModificationRequestIssue($issueId: ID!) {
          resolveModificationRequestIssue(issueId: $issueId)
        }
      `,
      { issueId: modificationRequestIssueId },
    );
  };

  public reopenModificationRequestIssue = async (
    modificationRequestIssueId: string,
  ): Promise<NonNullable<ReopenModificationRequestIssueMutation['reopenModificationRequestIssue']>> => {
    const result: ReopenModificationRequestIssueMutation = await this.graphqlClient.query(
      gql`
        mutation reopenModificationRequestIssue($issueId: ID!) {
          reopenModificationRequestIssue(issueId: $issueId) {
            id
            name
            resolved
            comments {
              id
              body
              assetIds
              createdAt
              user {
                ... on UserDirector {
                  id
                  name
                  email
                  profileImageUrl
                }
                ... on UserOptimizer {
                  id
                  name
                  email
                  profileImageUrl
                }
              }
            }
          }
        }
      `,
      { issueId: modificationRequestIssueId },
    );

    if (result.reopenModificationRequestIssue) {
      return result.reopenModificationRequestIssue;
    } else {
      throw new Error('reopenModificationRequestIssue is blank');
    }
  };

  public createModificationRequestDeliverable = async (
    targetModificationRequestId: string,
    fileId: string,
    attachedThumbnailId: string | null,
    intent: string,
  ): Promise<NonNullable<CreateModificationRequestDeliverableMutation['createModificationRequestDeliverable']>> => {
    try {
      const result: CreateModificationRequestDeliverableMutation = await this.graphqlClient.query(
        gql`
          mutation createModificationRequestDeliverable(
            $targetModificationRequestId: ID!
            $fileId: ID!
            $attachedThumbnailId: ID
            $intent: String!
          ) {
            createModificationRequestDeliverable(
              targetModificationRequestId: $targetModificationRequestId
              fileId: $fileId
              attachedThumbnailId: $attachedThumbnailId
              intent: $intent
            ) {
              id
              creative {
                id
                file {
                  id
                  originalFileName
                  url
                  mimeType
                  size
                  fastStart {
                    enabled
                  }
                  attribute {
                    aspectRatio {
                      size {
                        width
                        height
                      }
                    }
                    playbackTime {
                      seconds
                    }
                    volume {
                      maxVolume
                    }
                  }
                }
              }
              attachedThumbnail {
                id
                file {
                  id
                  originalFileName
                  url
                  mimeType
                  size
                  attribute {
                    aspectRatio {
                      size {
                        width
                        height
                      }
                    }
                    playbackTime {
                      seconds
                    }
                  }
                }
              }
              intent
              createdAt
            }
          }
        `,
        { targetModificationRequestId, fileId, attachedThumbnailId, intent },
      );

      if (result.createModificationRequestDeliverable) {
        return result.createModificationRequestDeliverable;
      } else {
        throw new Error('createModificationRequestDeliverable is blank');
      }
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'CreateDeliverableError') {
        throw new CreateModificationRequestDeliverableError();
      } else if (err instanceof GraphQLOperationFailed && err.type === 'AlreadyApprovedModificationRequestError') {
        throw new AlreadyApprovedModificationRequestError();
      }

      throw err;
    }
  };
}

export class CreateModificationRequestDeliverableError extends Error {
  public readonly name = 'CreateModificationRequestDeliverableError';
  public readonly message = 'Create modification request deliverable is failed.';

  constructor() {
    super();
  }
}
export class AlreadyApprovedModificationRequestError extends Error {
  public readonly name = 'AlreadyApprovedModificationRequestError';
  public readonly message = 'message.modification_request_already_approved';

  constructor() {
    super();
  }
}

export default ModificationRequestWebApi;
